import { Children, createContext, isValidElement, type ReactElement, type ReactNode } from 'react';

import { allSteps, type StepName } from '@/components/Step/registry';
import { type StepEnhanced, useSteps } from '@/hooks/useSteps';

import { Progress } from './Progress';
import { StepList } from './StepList';
import { type Step } from './types';
import { type StepProps } from '../Step';

const isStepName = (value: unknown): value is StepName =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(allSteps, value);

export const isStepElement = (child: unknown): child is ReactElement<StepProps> => {
  if (!isValidElement(child)) {
    return false;
  }

  const props = child.props as Record<string, unknown>;
  return isStepName(props.name);
};

const getStepListChild = (children: ReactNode): ReactElement<{ children: ReactNode }> | undefined => {
  const childArray = Children.toArray(children);
  return childArray.find(
    (child): child is ReactElement<{ children: ReactNode }> => isValidElement(child) && child.type === StepList
  );
};

const getStepElementsFromList = (stepListNode: ReactElement<{ children: ReactNode }> | undefined) => {
  if (!stepListNode) {
    return [];
  }

  return Children.toArray(stepListNode.props.children).filter(isStepElement);
};

const stepDefinitionsFromElements = (elements: ReactElement<StepProps>[]) => {
  return elements.map(element => ({
    name: element.props.name as StepName,
    ...(element.props.untraceable ? { untraceable: true as const } : {})
  }));
};

export const FlowContext = createContext<{
  steps: Step[];
  currentStep?: StepEnhanced;
  nextStep: () => void;
  previousStep: () => void;
}>({
  steps: [],
  currentStep: undefined,
  nextStep: () => {},
  previousStep: () => {}
});

type FlowProps = {
  children: ReactNode;
};

export const Flow = ({ children }: FlowProps) => {
  const stepListNode = getStepListChild(children);
  const stepElements = getStepElementsFromList(stepListNode);
  const steps = stepDefinitionsFromElements(stepElements);

  const { currentStep, nextStep, previousStep } = useSteps(steps);

  return (
    <FlowContext.Provider value={{ steps, currentStep, nextStep, previousStep }}>
      <section className='mx-auto flex w-full max-w-130 flex-1 flex-col justify-start px-6 pt-2'>{children}</section>
    </FlowContext.Provider>
  );
};

Flow.Progress = Progress;
Flow.StepList = StepList;
